import { GraphicEq, Mic, MicOff, Videocam, VideocamOff } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import {
  ConsoleLogger,
  DefaultDeviceController,
  LogLevel,
  RemovableAnalyserNode,
} from 'amazon-chime-sdk-js';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useVideoCall } from '../../../contexts/videoCall/context';
import { VideoCallActions } from '../../../contexts/videoCall/types';
import { logErrorAndReport } from '../../../lib/errorReporting';
import { getRoomUuidFromChannel } from '../../../lib/format';
import MixpanelClient, { TrackingEventV2 } from '../../../lib/tracking/mixpanel';
import Toast from '../../Toast/Toast';
import styles from './VideoPreview.module.css';

type Props = {
  hasPermissions: boolean;
  setHasPermissions: React.Dispatch<React.SetStateAction<boolean>>;
};

const VideoPreview: FC<Props> = ({ hasPermissions, setHasPermissions }) => {
  const { dispatchVideoCall, videoCallState } = useVideoCall();
  const {
    mediaSettings: { micEnabled, videoEnabled },
    videoPreview,
  } = videoCallState;

  const [volumeIndicatorActive, setVolumeIndicatorActive] = useState<boolean>(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const audioAnalyserNode = useRef<RemovableAnalyserNode | null>(null);
  const audioPreviewAnimationRef = useRef<number>();
  const audioPreviewStarted = useRef(false);
  const videoPreviewStarted = useRef(false);

  const deviceController = useMemo(() => {
    const logger = new ConsoleLogger('VideoPreview', LogLevel.ERROR);
    return new DefaultDeviceController(logger);
  }, []);

  const showCameraOnButton = useMemo(() => {
    return hasPermissions && videoEnabled;
  }, [hasPermissions, videoEnabled]);
  const showMicOnButton = useMemo(() => {
    return hasPermissions && micEnabled;
  }, [hasPermissions, micEnabled]);

  const sendMixpanelEvent = useCallback(
    (event: TrackingEventV2) => {
      if (!videoPreview) {
        return;
      }

      const { source, appointment, eventsChannel, currentParticipant } = videoPreview;
      MixpanelClient.trackEvent({
        eventName: event,
        properties: {
          source,
          appointmentId: appointment.coreAppointmentId,
          waitingRoomUuid: getRoomUuidFromChannel(eventsChannel),
          participantUuid: currentParticipant.uuid,
        },
      });
    },
    [videoPreview]
  );

  const setupPermissions = useCallback(async () => {
    try {
      // Ask for permissions
      await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      setHasPermissions(true);
    } catch (error) {
      Toast.error('Please enable mic and camera permissions to continue to the call');

      sendMixpanelEvent(TrackingEventV2.VideoCallLocalNoPermissionsEnabled);
      setHasPermissions(false);
    }
  }, [setHasPermissions, sendMixpanelEvent]);

  const startAudioPreview = useCallback(async () => {
    if (audioPreviewStarted.current) {
      return;
    }

    const audioDevices = await deviceController.listAudioInputDevices();
    if (!audioDevices || audioDevices.length === 0) {
      return;
    }

    // Get the audio device
    const defaultAudioDevice = audioDevices[0].deviceId;
    // Start audio input
    await deviceController.startAudioInput(defaultAudioDevice);

    audioAnalyserNode.current = deviceController.createAnalyserNodeForAudioInput();
    audioPreviewStarted.current = true;

    function analyzeAudio() {
      if (!audioAnalyserNode.current) {
        return;
      }

      const dataArray = new Uint8Array(audioAnalyserNode.current.fftSize);
      audioAnalyserNode.current.getByteTimeDomainData(dataArray);
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const value = dataArray[i] - 128;
        sum += value * value;
      }
      const rms = Math.sqrt(sum / dataArray.length);

      if (rms > 1) {
        setVolumeIndicatorActive(true);
      } else {
        setVolumeIndicatorActive(false);
      }

      if (audioPreviewAnimationRef.current) {
        cancelAnimationFrame(audioPreviewAnimationRef.current);
      }
      setTimeout(() => {
        audioPreviewAnimationRef.current = requestAnimationFrame(analyzeAudio);
      }, 1000);
    }

    analyzeAudio();
    sendMixpanelEvent(TrackingEventV2.VideoCallLocalParticipantEnableAudioPreview);
  }, [deviceController, sendMixpanelEvent]);

  const stopAudioPreview = useCallback(async () => {
    if (!audioPreviewStarted.current) {
      return;
    }

    if (audioPreviewAnimationRef.current) {
      cancelAnimationFrame(audioPreviewAnimationRef.current);
      audioPreviewAnimationRef.current = undefined;
    }

    if (audioAnalyserNode.current) {
      audioAnalyserNode.current.disconnect();
      await deviceController.stopAudioInput();
    }

    audioPreviewStarted.current = false;
    sendMixpanelEvent(TrackingEventV2.VideoCallLocalParticipantDisableAudioPreview);
  }, [deviceController, sendMixpanelEvent]);

  const startVideoPreview = useCallback(async () => {
    if (videoPreviewStarted.current) {
      return;
    }

    if (!videoRef.current) {
      logErrorAndReport({
        error: 'Could not find the video element when rendering the video preview',
      });
      return;
    }

    const videoDevices = await deviceController.listVideoInputDevices();
    if (!videoDevices || videoDevices.length === 0) {
      return;
    }

    // Get the video device
    const defaultVideoDevice = videoDevices[0].deviceId;
    // Start + Bind it to the video element
    await deviceController.startVideoInput(defaultVideoDevice);
    deviceController.startVideoPreviewForVideoInput(videoRef.current);

    videoPreviewStarted.current = true;
    sendMixpanelEvent(TrackingEventV2.VideoCallLocalParticipantEnableVideoPreview);
  }, [deviceController, sendMixpanelEvent]);

  const stopVideoPreview = useCallback(async () => {
    if (!videoPreviewStarted.current) {
      return;
    }

    if (videoRef.current) {
      deviceController.stopVideoPreviewForVideoInput(videoRef.current);
      await deviceController.stopVideoInput();
    }

    videoPreviewStarted.current = false;
    sendMixpanelEvent(TrackingEventV2.VideoCallLocalParticipantDisableVideoPreview);
  }, [deviceController, sendMixpanelEvent]);

  const toggleVideo = useCallback(() => {
    if (hasPermissions) {
      dispatchVideoCall({ type: VideoCallActions.ToggleVideo, payload: null });
    }
  }, [hasPermissions, dispatchVideoCall]);

  const toggleMic = useCallback(() => {
    if (hasPermissions) {
      dispatchVideoCall({ type: VideoCallActions.ToggleMic, payload: null });
    }
  }, [hasPermissions, dispatchVideoCall]);

  useEffect(() => {
    if (!hasPermissions) {
      return;
    }

    if (micEnabled) {
      startAudioPreview();
    }

    return () => {
      stopAudioPreview();
    };
  }, [micEnabled, hasPermissions, startAudioPreview, stopAudioPreview]);

  useEffect(() => {
    if (!hasPermissions) {
      return;
    }

    if (videoEnabled) {
      startVideoPreview();
    }

    return () => {
      stopVideoPreview();
    };
  }, [videoEnabled, hasPermissions, startVideoPreview, stopVideoPreview]);

  useEffect(() => {
    setupPermissions();
  }, [setupPermissions]);

  return (
    <Box className={styles.videoPreview}>
      <Box className={styles.video}>
        {hasPermissions ? (
          <>
            {videoEnabled ? (
              <video ref={videoRef} className={styles.videoElement} />
            ) : (
              <Box className={styles.warningMessage}>
                <Typography variant='body2'>Camera is turned off</Typography>
              </Box>
            )}
          </>
        ) : (
          <Box className={styles.warningMessage}>
            <Typography variant='body2'>
              Camera access required
              <br />
              Please enable permissions
            </Typography>
          </Box>
        )}

        {/* Volume indicator */}
        <Box className={styles.topSectionItems}>
          <IconButton className={styles.volumeIndicator}>
            <GraphicEq
              className={[
                styles.volumeIndicatorIcon,
                volumeIndicatorActive ? styles.volumeIndicatorAnimationActive : '',
              ].join(' ')}
            />
          </IconButton>
        </Box>

        {/* Control buttons positioned at bottom */}
        <Box className={styles.controlButtonsSection}>
          <IconButton
            className={[
              styles.actionButton,
              showCameraOnButton ? styles.buttonOn : styles.buttonOff,
            ].join(' ')}
            onClick={toggleVideo}>
            {showCameraOnButton ? (
              <Videocam htmlColor='white' />
            ) : (
              <VideocamOff htmlColor='#e31414' />
            )}
          </IconButton>

          <IconButton
            className={[
              styles.actionButton,
              showMicOnButton ? styles.buttonOn : styles.buttonOff,
            ].join(' ')}
            onClick={toggleMic}>
            {showMicOnButton ? <Mic htmlColor='white' /> : <MicOff htmlColor='#e31414' />}
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default VideoPreview;
